import React, { useEffect, useState } from "react"
import { Link, useNavigate, useLocation } from "react-router-dom"
import moment from "moment"
import { FaEye, FaCheckCircle, FaExclamationTriangle } from "react-icons/fa"
import { getUsers } from "../../requesters/Backend/UserRequester"
import Pagination from "../Pagination"

const PER_PAGE = 25

function BackendUsersDashboard({ baseUrl, currentUser }) {
  const location = useLocation()
  const navigate = useNavigate()
  const { pathname, search } = location
  const initialParams = new URLSearchParams(location.search)

  const [userList, setUserList] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(parseInt(initialParams.get("page") || "1", 10))
  const [query, setQuery] = useState(initialParams.get("search") || "")
  const [debouncedQuery, setDebouncedQuery] = useState(initialParams.get("search") || "")
  const [confirmationStatus, setConfirmationStatus] = useState(initialParams.get("confirmation_status") || "")

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(t)
  }, [query])

  useEffect(() => {
    const params = { page, per_page: PER_PAGE }
    if (debouncedQuery) params.search = debouncedQuery
    if (confirmationStatus) params.confirmation_status = confirmationStatus

    getUsers(baseUrl, params, currentUser).then(res => {
      setUserList(res?.response || [])
      setTotal(parseInt(res?.headers?.["x-total"] || "0", 10))
      return null
    }).catch(err => {
      // eslint-disable-next-line no-console
      console.log(err)
    })
  }, [page, debouncedQuery, confirmationStatus, baseUrl, currentUser])

  useEffect(() => {
    const params = new URLSearchParams()
    params.set("page", String(page))
    if (debouncedQuery) params.set("search", debouncedQuery)
    if (confirmationStatus) params.set("confirmation_status", confirmationStatus)
    const next = `?${params.toString()}`
    if (search === next) return
    navigate({ pathname, search: next }, { replace: true })
  }, [page, debouncedQuery, confirmationStatus, navigate, pathname, search])

  const handleSearch = event => {
    setQuery(event.target.value)
    setPage(1)
  }

  const handleConfirmationStatus = event => {
    setConfirmationStatus(event.target.value)
    setPage(1)
  }

  return (
    <div className="content">
      <div className="container-fluid">
        <div className="page-header">
          <div className="d-flex justify-content-between align-items-center">
            <h2>Users</h2>
          </div>
        </div>
        <div className="table-filters d-inline-flex">
          <input
            type="search"
            className="form-control"
            placeholder="e.g. John Doe"
            value={query}
            onChange={handleSearch} />
          <select
            className="form-control ml-2"
            value={confirmationStatus}
            onChange={handleConfirmationStatus}
            aria-label="Filter by email confirmation">
            <option value="">All users</option>
            <option value="confirmed">Confirmed</option>
            <option value="unconfirmed">Unconfirmed</option>
          </select>
        </div>
        {userList.length === 0 ? (
          <p className="text-muted mt-3 text-center">No users found</p>
        ) : (
          <>
            <div className="list-table-wrap">
              <div className="table-responsive">
                <table className="table list-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Unit</th>
                      <th>Program</th>
                      <th>Email Confirmed</th>
                      <th>Created At</th>
                      <th><span className="sr-only">Actions</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {userList.map(user => (
                      <tr key={user.id}>
                        <td>{user.name}</td>
                        <td className="cell-muted">{user.email}</td>
                        <td>
                          {user.unit ? (
                            <Link to={`/backend/units/${user.unit.id}`}>{user.unit.name}</Link>
                          ) : "-"}
                        </td>
                        <td>
                          {user.active_program ? (
                            <Link to={`/backend/programs/${user.active_program.id}`}>{user.active_program.name}</Link>
                          ) : "-"}
                        </td>
                        <td>
                          {user.confirmed_at ? (
                            <span className="text-success">
                              <FaCheckCircle className="mr-1" />
                              {moment(user.confirmed_at).format("DD MMM YYYY")}
                            </span>
                          ) : (
                            <span className="text-danger">
                              <FaExclamationTriangle className="mr-1" />
                              Unconfirmed
                            </span>
                          )}
                        </td>
                        <td className="cell-muted">{user.created_at ? moment(user.created_at).format("DD MMM YYYY") : ""}</td>
                        <td className="text-center">
                          <Link to={`/backend/users/${user.id}`} className="text-muted" title="View user">
                            <FaEye />
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            <Pagination
              key={`${debouncedQuery}-${confirmationStatus}`}
              totalRows={total}
              pageChangeHandler={setPage}
              rowsPerPage={PER_PAGE}
              initialPage={page} />
          </>
        )}
      </div>
    </div>
  )
}

export default BackendUsersDashboard
